/**
 * HALO Virtual Office Platform - Team Pulse Hook
 *
 * React hook for managing real-time team wellness data
 * with Supabase subscriptions
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  realtimeManager,
  fetchTeamMembers,
  fetchActiveAlerts,
  acknowledgeAlert,
  type WellnessAlertRow,
} from "../lib/supabase";
import type { EmployeeWellnessData } from "../components/EmployeeCard";

// ============================================================================
// TYPES
// ============================================================================

export interface TeamPulseState {
  employees: EmployeeWellnessData[];
  alerts: WellnessAlertRow[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

export interface TeamPulseActions {
  refresh: () => Promise<void>;
  acknowledgeAlert: (alertId: string) => Promise<void>;
  getEmployeeById: (id: string) => EmployeeWellnessData | undefined;
}

export interface UseTeamPulseOptions {
  organizationId: string;
  userId: string;
  autoRefreshInterval?: number; // milliseconds, 0 to disable
  enableRealtime?: boolean;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useTeamPulse(
  options: UseTeamPulseOptions,
): TeamPulseState & TeamPulseActions {
  const {
    organizationId,
    userId,
    autoRefreshInterval = 15000, // 15 seconds default
    enableRealtime = true,
  } = options;

  // State
  const [employees, setEmployees] = useState<EmployeeWellnessData[]>([]);
  const [alerts, setAlerts] = useState<WellnessAlertRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Refs for cleanup
  const wellnessUnsubscribeRef = useRef<(() => void) | null>(null);
  const alertUnsubscribeRef = useRef<(() => void) | null>(null);
  const presenceUnsubscribeRef = useRef<(() => void) | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onlineUsersRef = useRef<Set<string>>(new Set());

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const refresh = useCallback(async () => {
    if (!organizationId) return;

    try {
      setError(null);

      const [teamData, alertsData] = await Promise.all([
        fetchTeamMembers(organizationId),
        fetchActiveAlerts(organizationId),
      ]);
      console.log("[useTeamPulse] Team data:", teamData);

      setEmployees(teamData);
      setAlerts(alertsData);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("[useTeamPulse] Error refreshing data:", err);
      setError("Failed to load team data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  // ============================================================================
  // REAL-TIME UPDATES
  // ============================================================================

  /**
   * Handle incoming wellness update from real-time subscription
   */
  const handleWellnessUpdate = useCallback((data: EmployeeWellnessData) => {
    setEmployees((prev) => {
      const index = prev.findIndex((e) => e.id === data.id);
      if (index >= 0) {
        // Update existing employee (merge to preserve metadata like name/email/avatar)
        const updated = [...prev];
        updated[index] = {
          ...prev[index],
          ...data,
          // Don't force online - let presence handle it
          // is_online: true,
          is_online: onlineUsersRef.current.has(data.id),
        };
        return updated;
      } else {
        // Add new employee
        return [...prev, data];
      }
    });
    setLastUpdated(new Date());
  }, []);

  /**
   * Handle incoming alert from real-time subscription
   */
  const handleNewAlert = useCallback((alert: WellnessAlertRow) => {
    setAlerts((prev) => [alert, ...prev]);
  }, []);

  /**
   * Handle presence changes - update online status for employees
   */
  const handlePresenceChange = useCallback((onlineUserIds: Set<string>) => {
    onlineUsersRef.current = onlineUserIds;
    const now = new Date();
    setEmployees((prev) =>
      prev.map((emp) => {
        const isOnline = onlineUserIds.has(emp.id);
        return {
          ...emp,
          is_online: isOnline,
          // If user is online via presence, update their last_updated to prevent staleness timeout
          last_updated: isOnline ? now : emp.last_updated,
        };
      }),
    );
    console.log(
      "[useTeamPulse] Presence update - online users:",
      Array.from(onlineUserIds),
    );
  }, []);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const handleAcknowledgeAlert = useCallback(
    async (alertId: string) => {
      const success = await acknowledgeAlert(alertId, userId);
      if (success) {
        setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      }
    },
    [userId],
  );

  const getEmployeeById = useCallback(
    (id: string) => employees.find((e) => e.id === id),
    [employees],
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Initial data fetch
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!enableRealtime || !organizationId) return;

    // Subscribe to wellness updates
    wellnessUnsubscribeRef.current = realtimeManager.subscribeToWellnessUpdates(
      organizationId,
      handleWellnessUpdate,
    );

    // Subscribe to alerts
    alertUnsubscribeRef.current = realtimeManager.subscribeToAlerts(
      organizationId,
      handleNewAlert,
    );

    // Subscribe to presence for online status and real-time broadcasts
    presenceUnsubscribeRef.current = realtimeManager.subscribeToPresence(
      organizationId,
      handlePresenceChange,
      handleWellnessUpdate,
    );

    return () => {
      wellnessUnsubscribeRef.current?.();
      alertUnsubscribeRef.current?.();
      presenceUnsubscribeRef.current?.();
    };
  }, [
    organizationId,
    enableRealtime,
    handleWellnessUpdate,
    handleNewAlert,
    handlePresenceChange,
  ]);

  // Set up auto-refresh interval
  useEffect(() => {
    if (autoRefreshInterval <= 0) return;

    refreshIntervalRef.current = setInterval(refresh, autoRefreshInterval);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefreshInterval, refresh]);

  // Mark offline employees after timeout (more aggressive - 30 seconds)
  useEffect(() => {
    const checkOffline = setInterval(() => {
      // const thirtySecondsAgo = Date.now() - 30 * 1000; // Reduced from 2 minutes to 30 seconds

      setEmployees((prev) =>
        prev.map((employee) => {
          // Check if user is in the presence set
          const isInPresence = onlineUsersRef.current.has(employee.id);

          // If NOT in presence set, mark as offline immediately
          if (!isInPresence && employee.is_online) {
            console.log(
              `[useTeamPulse] Marking ${employee.name} offline - not in presence set`,
            );
            return { ...employee, is_online: false };
          }

          // If in presence set but last_updated is stale, also mark offline
          // This handles cases where presence didn't fire leave event
          // if (
          //   isInPresence &&
          //   employee.last_updated.getTime() < thirtySecondsAgo &&
          //   employee.is_online
          // ) {
          //   console.log(
          //     `[useTeamPulse] Marking ${employee.name} offline - stale last_updated`,
          //   );
          //   return { ...employee, is_online: false };
          // }

          return employee;
        }),
      );
    }, 5000); // Check every 5 seconds (more frequent)

    return () => clearInterval(checkOffline);
  }, []);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    employees,
    alerts,
    isLoading,
    error,
    lastUpdated,
    refresh,
    acknowledgeAlert: handleAcknowledgeAlert,
    getEmployeeById,
  };
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to get wellness summary statistics
 */
export function useWellnessSummary(employees: EmployeeWellnessData[]) {
  const employeesWithData = employees.filter(
    (e) => e.posture_score !== null && e.fatigue_index !== null,
  );

  return {
    totalOnline: employees.filter((e) => e.is_online).length,
    totalOffline: employees.filter((e) => !e.is_online).length,
    highFatigueCount: employees.filter(
      (e) => e.fatigue_index !== null && e.fatigue_index > 0.7,
    ).length,
    poorPostureCount: employees.filter(
      (e) => e.posture_score !== null && e.posture_score < 50,
    ).length,
    onPhoneCount: employees.filter((e) => e.hand_activity === "Phone Detected")
      .length,
    averagePosture:
      employeesWithData.length > 0
        ? Math.round(
            employeesWithData.reduce(
              (sum, e) => sum + (e.posture_score || 0),
              0,
            ) / employeesWithData.length,
          )
        : null,
    averageFatigue:
      employeesWithData.length > 0
        ? Math.round(
            (employeesWithData.reduce(
              (sum, e) => sum + (e.fatigue_index || 0),
              0,
            ) /
              employeesWithData.length) *
              100,
          ) / 100
        : null,
    employeesWithDataCount: employeesWithData.length,
  };
}

/**
 * Hook to filter employees by criteria
 */
export function useFilteredEmployees(
  employees: EmployeeWellnessData[],
  filters: {
    onlineOnly?: boolean;
    hasAlerts?: boolean;
    department?: string;
    searchQuery?: string;
  },
) {
  return employees.filter((employee) => {
    if (filters.onlineOnly && !employee.is_online) return false;

    if (filters.hasAlerts) {
      const hasAlert =
        (employee.fatigue_index !== null && employee.fatigue_index > 0.7) ||
        (employee.posture_score !== null && employee.posture_score < 50) ||
        employee.hand_activity === "Phone Detected";
      if (!hasAlert) return false;
    }

    if (filters.department && employee.department !== filters.department) {
      return false;
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      if (
        !employee.name.toLowerCase().includes(query) &&
        !employee.email.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    return true;
  });
}

export default useTeamPulse;
